import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";

import { Kbd } from "../components/Kbd";
import { Modal } from "../components/Modal";
import { usePreferences } from "../data/api/preferences";
import { useSearch } from "../data/api/search";
import { useActivityTasks } from "../data/api/tasks";
import type { PaletteRecent } from "../data/api/types";
import { Icon } from "../icons";
import styles from "./CommandPalette.module.css";
import { cx } from "../lib/cx";
import { paths } from "../routing/paths";
import { useActiveRepoId } from "../routing/useNav";

type PaletteItem = {
  readonly hint?: string;
  // Every palette row goes somewhere, so every row is a link: the URL it will
  // navigate to. Rows that can't resolve one are not listed.
  readonly href: string;
  readonly ico: ReactNode;
  readonly id: string;
  readonly keywords?: string;
  readonly label: string;
  // Set on repo/task targets: activating one records it into the user's recents.
  readonly recent?: PaletteRecent;
  readonly section: string;
};

// A click that the browser would turn into a new tab or window: it leaves the
// palette open and lets the anchor do its native thing.
const opensElsewhere = (e: MouseEvent): boolean =>
  e.button !== 0 || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey;

export type CommandPaletteProps = {
  readonly onClose: () => void;
};

export const CommandPalette = ({ onClose }: CommandPaletteProps) => {
  const navigate = useNavigate();
  // "Start new agent" is scoped to the repo the route is in, when it is in one
  // — the same derivation the `N` shortcut uses, which is the hint this row
  // advertises (command-palette-and-search.md §Actions).
  const activeRepoId = useActiveRepoId();
  const { preferences, update } = usePreferences();
  const recents = preferences.command_palette_recents;
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce the query so we don't hit /search on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 140);
    return () => clearTimeout(timer);
  }, [query]);

  const { results } = useSearch(debounced);
  // The user's tasks drive the status-derived default sections (command-palette
  // -and-search.md §Sections): the "Needs your input" list + the "Review next
  // PR" action count. Only used while the query is empty.
  const { tasks } = useActivityTasks();
  const needsInput = useMemo(
    () => tasks.filter((t) => t.status === "needs-input"),
    [tasks],
  );
  const reviewTasks = useMemo(
    () => tasks.filter((t) => t.status === "review"),
    [tasks],
  );

  // Client-side commands (they run local navigation, not server data) — always
  // available, filtered locally by the query.
  const commands = useMemo<ReadonlyArray<PaletteItem>>(() => {
    const base: PaletteItem[] = [
      {
        hint: "N",
        href: paths.newAgent(activeRepoId),
        ico: <Icon.Plus size={14} />,
        id: "new",
        label: "Start new agent",
        section: "Actions",
      },
      {
        hint: "G H",
        href: paths.dashboard(),
        ico: <Icon.Home size={14} />,
        id: "go-home",
        label: "Go to repositories",
        section: "Actions",
      },
      {
        hint: "G S",
        href: paths.settings(),
        ico: <Icon.Cog size={14} />,
        id: "settings",
        label: "Open settings",
        section: "Actions",
      },
    ];
    // Shown only when tasks await review; jumps to the first one.
    const first = reviewTasks[0];
    if (first) {
      base.push({
        hint: "↵",
        href: paths.task(first.repo, first.displayId),
        ico: <Icon.PullRequest size={14} />,
        id: "review-next",
        keywords: "review pr pull request",
        label: `Review next PR (${reviewTasks.length} waiting)`,
        section: "Actions",
      });
    }
    return base;
  }, [activeRepoId, reviewTasks]);

  // One row per task currently blocked on the user (query empty only).
  const needsInputItems = useMemo<ReadonlyArray<PaletteItem>>(() => {
    if (query) {
      return [];
    }
    return needsInput.map((t): PaletteItem => ({
      hint: "↵",
      href: paths.task(t.repo, t.displayId),
      ico: <Icon.Spark size={14} />,
      id: `needs:${t.id}`,
      label: t.title,
      section: "Needs your input",
    }));
  }, [query, needsInput]);

  // Repos + tasks from the server search (full-text ranked; the default set
  // when the query is empty). Task refs carry the repo id for routing; a task
  // without one has no URL to offer, so it is left out rather than listed as a
  // row that goes nowhere.
  const serverItems = useMemo<ReadonlyArray<PaletteItem>>(
    () =>
      results
        // A task needs both halves of its URL to be routable, so a row missing
        // either is dropped rather than rendered as a dead link — which is also
        // what justifies the assertions below.
        .filter(
          (r) =>
            (r.type === "repo" && r.ref) ||
            (r.type === "task" && r.ref?.repo_public_id && r.ref.display_id),
        )
        .map((r): PaletteItem => {
          const ref = r.ref!;
          if (r.type === "repo") {
            return {
              hint: "↵",
              href: paths.repo(ref.public_id),
              ico: <Icon.Repo size={14} />,
              id: `repo:${ref.public_id}`,
              label: r.label,
              recent: { kind: "repo", label: r.label, ref_id: ref.public_id },
              section: "Repositories",
            };
          }
          return {
            hint: "↵",
            href: paths.task(ref.repo_public_id!, ref.display_id!),
            ico: <Icon.Spark size={14} />,
            id: `task:${ref.public_id}`,
            label: r.label,
            recent: {
              kind: "task",
              label: r.label,
              // The recents entry is replayed straight into `paths.task`, so it
              // stores what that takes — the label, not the id.
              ref_id: ref.display_id!,
              repo_id: ref.repo_public_id ?? null,
            },
            section: "Tasks",
          };
        }),
    [results],
  );

  // Recent items (command-palette-and-search.md §Recent items) — persisted on
  // the user's preferences, shown only while the query is empty.
  const recentItems = useMemo<ReadonlyArray<PaletteItem>>(() => {
    if (query) {
      return [];
    }
    return recents
      .filter((r) => r.kind === "repo" || r.repo_id)
      .map((r): PaletteItem => ({
        hint: "↵",
        href:
          r.kind === "repo"
            ? paths.repo(r.ref_id)
            : paths.task(r.repo_id!, r.ref_id),
        ico:
          r.kind === "repo" ? (
            <Icon.Repo size={14} />
          ) : (
            <Icon.Spark size={14} />
          ),
        id: `recent:${r.kind}:${r.ref_id}`,
        label: r.label,
        recent: r,
        section: "Recent",
      }));
  }, [query, recents]);

  const filteredCommands = useMemo<ReadonlyArray<PaletteItem>>(() => {
    if (!query) {
      return commands;
    }
    const q = query.toLowerCase();
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ""}`.toLowerCase().includes(q),
    );
  }, [query, commands]);

  const items = useMemo<ReadonlyArray<PaletteItem>>(
    () => [
      ...recentItems,
      ...filteredCommands,
      ...needsInputItems,
      ...serverItems,
    ],
    [recentItems, filteredCommands, needsInputItems, serverItems],
  );
  const active = items.length > 0 ? Math.min(index, items.length - 1) : 0;

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-i="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  // Record an activated repo/task into recents: newest first, de-duped by ref,
  // capped at 5, persisted to the user's preferences.
  const recordRecent = (r: PaletteRecent): void => {
    const next = [
      r,
      ...recents.filter((x) => !(x.kind === r.kind && x.ref_id === r.ref_id)),
    ].slice(0, 5);
    update({ command_palette_recents: next });
  };

  // The bookkeeping around an activation, shared by the click and the keyboard
  // paths; the navigation itself is the anchor's job (or `navigate` on Enter).
  const record = (item: PaletteItem): void => {
    if (item.recent) {
      recordRecent(item.recent);
    }
  };

  const exec = (item: PaletteItem | undefined): void => {
    if (!item) {
      return;
    }
    record(item);
    void navigate(item.href);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      exec(items[active]);
    }
  };

  let lastSection: string | null = null;

  return (
    <Modal
      className={styles.palette}
      onClose={onClose}
      open
      title="Command palette"
    >
      <div className={styles.inputWrap}>
        <Icon.Search size={16} style={{ color: "var(--ink-3)" }} />
        <input
          autoFocus
          className={styles.input}
          onChange={(e) => {
            setQuery(e.target.value);
            setIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search or run a command…"
          value={query}
        />
        <Kbd>esc</Kbd>
      </div>
      <div className={styles.list} ref={listRef}>
        {items.map((item, i) => {
          const header = item.section !== lastSection ? item.section : null;
          lastSection = item.section;
          return (
            <div key={item.id}>
              {header ? <div className={styles.section}>{header}</div> : null}
              <Link
                className={cx(styles.item, i === active && styles.active)}
                data-i={i}
                onClick={(e) => {
                  record(item);
                  if (!opensElsewhere(e)) {
                    onClose();
                  }
                }}
                onMouseEnter={() => setIndex(i)}
                to={item.href}
              >
                <span className={styles.ico}>{item.ico}</span>
                <span className={styles.grow}>{item.label}</span>
                {item.hint ? (
                  <span className={styles.hint}>{item.hint}</span>
                ) : null}
              </Link>
            </div>
          );
        })}
        {items.length === 0 && <div className={styles.empty}>No matches.</div>}
      </div>
    </Modal>
  );
};
